import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import Button from '../components/Button';
import Chip from '../components/Chip';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FormModal from '@shared/ui/FormModal';
import SearchField from '../components/SearchField';
import type { TemplateDefinition } from '../types/outreach';
import { usePageTitle } from '../hooks/usePageTitle';
import toast from 'react-hot-toast';
import RefreshButton from '../components/RefreshButton';
import { StandardTableHeader } from '../components/StandardTable';
import { EditIcon } from '../components/icons';
import ActionsHeaderHelp from '../components/ActionsHeaderHelp';
import { outreachTypeIcon } from '../components/Outreach/outreachTypeIcons';

interface OutreachTemplate {
  id: string;
  name: string;
  description: string | null;
  type: 'campaign' | 'survey' | 'poll';
  definition_json: TemplateDefinition;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const getTypeIcon = (type: string) => outreachTypeIcon(type);

const getTypeColor = (type: string): 'primary' | 'success' | 'info' => {
  switch (type) {
    case 'campaign':
      return 'primary';
    case 'survey':
      return 'success';
    case 'poll':
      return 'info';
    default:
      return 'primary';
  }
};

const OutreachTemplatesAdminPage: React.FC = () => {
  usePageTitle('System Outreach Templates', 'Manage system-wide outreach templates');

  const [templates, setTemplates] = useState<OutreachTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<OutreachTemplate | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<OutreachTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'survey' as 'campaign' | 'survey' | 'poll'
  });

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/outreach-unified/templates', {
        params: { system_only: true }
      });
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormData({ name: '', description: '', type: 'survey' });
    setShowBuilder(true);
  };

  const handleEdit = (template: OutreachTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      type: template.type
    });
    setShowBuilder(true);
  };

  const handleSave = async (definition: TemplateDefinition) => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      setSaving(true);

      if (editingTemplate) {
        await api.put(`/outreach-unified/templates/${editingTemplate.id}`, {
          name: formData.name,
          description: formData.description,
          type: formData.type,
          definition_json: definition
        });
        toast.success('Template updated');
      } else {
        await api.post('/outreach-unified/templates', {
          name: formData.name,
          description: formData.description,
          type: formData.type,
          definition_json: definition,
          is_system: true
        });
        toast.success('Template created');
      }

      setShowBuilder(false);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      await api.delete(`/outreach-unified/templates/${templateToDelete.id}`);
      toast.success('Template deleted');
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
          System Outreach Templates
        </h1>
        <p className="text-text-secondary dark:text-text-dark-secondary mt-1">
          Manage system-wide templates available to all organizations
        </p>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <SearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search templates..."
          className="flex-1 max-w-md"
        />
        <div className="flex gap-2">
          <RefreshButton onRefresh={fetchTemplates} />
          <Button variant="primary" onClick={handleCreateNew}>
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Templates Table */}
      <div className="card overflow-hidden">
        <table className="w-full">
          <StandardTableHeader>
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Template
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Type
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Questions
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Status
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                <span className="inline-flex items-center gap-1.5">
                  Actions
                  <ActionsHeaderHelp tone="default" icons={['edit', 'delete']} />
                </span>
              </th>
            </tr>
          </StandardTableHeader>
          <tbody className="divide-y divide-border-light dark:divide-border-dark">
            {filteredTemplates.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-text-secondary dark:text-text-dark-secondary">
                  {searchQuery ? 'No templates match your search' : 'No system templates yet'}
                </td>
              </tr>
            ) : (
              filteredTemplates.map((template) => {
                const TypeIcon = getTypeIcon(template.type);
                return (
                  <tr key={template.id} className="hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <TypeIcon className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
                        <div>
                          <div className="font-medium text-text-primary dark:text-text-dark-primary">
                            {template.name}
                          </div>
                          {template.description && (
                            <div className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-1">
                              {template.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Chip
                        label={template.type.charAt(0).toUpperCase() + template.type.slice(1)}
                        variant={getTypeColor(template.type)}
                        size="sm"
                      />
                    </td>
                    <td className="px-4 py-4 text-text-secondary dark:text-text-dark-secondary">
                      {template.definition_json?.questions?.length || 0}
                    </td>
                    <td className="px-4 py-4">
                      <Chip
                        label={template.is_active ? 'Active' : 'Inactive'}
                        variant={template.is_active ? 'success' : 'neutral'}
                        size="sm"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleEdit(template)}
                          title={`Edit ${template.name}`}
                          ariaLabel={`Edit ${template.name}`}
                        >
                          <EditIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setTemplateToDelete(template);
                            setDeleteConfirmOpen(true);
                          }}
                          title={`Delete ${template.name}`}
                          ariaLabel={`Delete ${template.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Template Builder Modal */}
      {showBuilder && (
        <FormModal
          isOpen={true}
          title={editingTemplate ? 'Edit template' : 'New template'}
          onClose={() => setShowBuilder(false)}
          onSubmit={() =>
            handleSave(
              editingTemplate?.definition_json || {
                questions: [],
                demographics: { enabled: false, required_fields: [], optional_fields: [] },
                post_submission_message: '<p>Thank you for your response!</p>',
              }
            )
          }
          size="sm"
          submitLabel={editingTemplate ? 'Save changes' : 'Create template'}
          isSubmitting={saving}
          submitDisabled={saving || !formData.name.trim()}
          isDirty={formData.name.trim().length > 0}
        >
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input"
                  placeholder="Template name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input"
                  rows={2}
                  placeholder="Brief description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'campaign' | 'survey' | 'poll' }))}
                  className="input"
                >
                  <option value="campaign">Campaign</option>
                  <option value="survey">Survey</option>
                  <option value="poll">Poll</option>
                </select>
              </div>
            </div>
        </FormModal>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={deleteConfirmOpen}
        title="Delete Template"
        message={`Are you sure you want to delete "${templateToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setTemplateToDelete(null);
        }}
        variant="danger"
      />
    </div>
  );
};

export default OutreachTemplatesAdminPage;
